import {runInAction} from 'mobx';
import {getLang} from '../../../Localization';
import {common, priority} from '@priority-software/priority-proto-api/lib';
import {formatNumber, cleanThousandSigns} from '../../../features/Form/utils/number';
import {
    decodeStringDateByType,
    formatDate,
    localizedWeekDays
} from '../../../features/Form/utils/date';
import {shiftUTCDateTimeToCurrentTimeZone} from '@priority-software/priority-utils/lib/date';
import {
    format6Time,
    formatStringToTimeObj,
    formatTime,
    getDataTime6Format
} from '../../../features/Form/utils/time';
import {RecordEditState, UpdateActions} from '../enums';
import {Form} from '../Form';
import {Column} from '../Column';
import {IFormViewTab} from '../../../features/Form/components/FormView/useFormView';

export const getOrigValue = (Value, column) => {
    const lang = getLang();
    let value;

    if (!column || !Value) {
        return '';
    }
    const decimal = Number(column.Decimal);

    switch (column.Type) {
        case priority.netitems.form.ColumnType.Real:
            if (Value.Real) {
                // BALANCE3 is a special case, it should be displayed with an opposite sign
                // identifing the field by Name is a temporary solution
                // we expect to receive an additional property for this in the column object
                let _value = Value.Real;
                if (column.Name === 'BALANCE3') {
                    _value = _value * -1;
                }
                value = formatNumber(_value?.toString(), decimal);
            }
            break;
        case priority.netitems.form.ColumnType.Int:
            if (Value.Int) {
                value = formatNumber(Value.Int?.toString(), decimal);
            } else if (Value.Shifted) {
                value = formatNumber(Value.Shifted?.toString(), decimal);
            }
            break;
        case priority.netitems.form.ColumnType.Unsigned:
            if (Value.Int) {
                value = formatNumber(Value.Int?.toString(), decimal);
            }
            break;
        case priority.netitems.form.ColumnType.Str:
        case priority.netitems.form.ColumnType.Rstr:
        case priority.netitems.form.ColumnType.Char:
            value = Value.Str;
            break;
        case priority.netitems.form.ColumnType.Boolean:
            value = Value.Boolean;
            break;
        case priority.netitems.form.ColumnType.Date10:
        case priority.netitems.form.ColumnType.Date8:
            if (Value.Date) {
                const {year, month, day} = Value.Date;
                const date = new Date(year, month - 1, day);
                value = formatDate(date, column.Type);
            }
            break;
        case priority.netitems.form.ColumnType.Date14:
            if (Value.DateTime) {
                const {year, month, day, hours, minutes} = Value.DateTime;
                const dateTime = new Date(year, month - 1, day, hours, minutes);
                shiftUTCDateTimeToCurrentTimeZone(dateTime);
                value = formatDate(dateTime, column.Type);
            }
            break;
        case priority.netitems.form.ColumnType.Time5:
            if (Value.Time) {
                const {hours, minutes} = Value.Time;
                value = formatTime(hours, minutes, lang === '3');
            }
            break;
        case priority.netitems.form.ColumnType.Time6:
            if (Value.Time) {
                const {hours, minutes} = Value.Time;
                value = format6Time(hours, minutes);
            }
            break;
        case priority.netitems.form.ColumnType.Day:
            if (Value.Int) {
                value = localizedWeekDays[Value.Int - 1];
            }
            break;
        case priority.netitems.form.ColumnType.Attachment:
            if (Value.File) {
                value = Value.File.Path;
            }
            break;
        default:
            value = '';
            break;
    }

    return value;
};

export const mapValueByColumnType = (value: any, column?: priority.netitems.form.IColumn) => {
    if (!column) return {};
    switch (column.Type) {
        case priority.netitems.form.ColumnType.Attachment:
            return {
                ColumnID: column.ID,
                Value: new common.Constant({File: value})
            };
        case priority.netitems.form.ColumnType.Real:
            return {
                ColumnID: column.ID,
                Value: new common.Constant({Real: Number(value)})
            };
        case priority.netitems.form.ColumnType.Int:
            const cleanValue = cleanThousandSigns(value);
            if (Number(column.Decimal) > 0) {
                return {
                    ColumnID: column.ID,
                    Value: new common.Constant({Shifted: Number(cleanValue)})
                };
            }
            return {
                ColumnID: column.ID,
                Value: new common.Constant({Int: Number(cleanValue)})
            };
        case priority.netitems.form.ColumnType.Unsigned:
            return {
                ColumnID: column.ID,
                Value: new common.Constant({Int: value})
            };
        case priority.netitems.form.ColumnType.Str:
        case priority.netitems.form.ColumnType.Rstr:
        case priority.netitems.form.ColumnType.Char:
            const formattedString =
                column.Zoom === priority.netitems.form.ZoomType.EMailZoom
                    ? value.toLowerCase()
                    : value;
            return {
                ColumnID: column.ID,
                Value: new common.Constant({Str: formattedString})
            };
        case priority.netitems.form.ColumnType.Boolean:
            return {
                ColumnID: column.ID,
                Value: new common.Constant({Boolean: value})
            };
        case priority.netitems.form.ColumnType.Date10:
        case priority.netitems.form.ColumnType.Date8:
            const dateStr = value || '01/01/1988'; // 01/01/88 is the zero date for priority (David Mirochnik note)
            return {
                ColumnID: column.ID,
                Value: new common.Constant({
                    Date: decodeStringDateByType(dateStr, column.Type)
                })
            };
        case priority.netitems.form.ColumnType.Date14:
            return {
                ColumnID: column.ID,
                Value: new common.Constant({
                    DateTime: decodeStringDateByType(value, column.Type)
                })
            };
        case priority.netitems.form.ColumnType.Time5:
            const dateObj = formatStringToTimeObj(value);

            return {
                ColumnID: column.ID,
                Value: new common.Constant({
                    Time: {
                        hours: dateObj.getHours(),
                        minutes: dateObj.getMinutes()
                    }
                })
            };
        case priority.netitems.form.ColumnType.Time6:
            return {
                ColumnID: column.ID,
                Value: new common.Constant({Time: getDataTime6Format(value)})
            };
        case priority.netitems.form.ColumnType.Day:
            return {
                ColumnID: column.ID,
                Value: new common.Constant({Int: value})
            };
        default:
            return {};
    }
};

type CheckFieldParams = {
    newCellIndex?: number;
    newRowIndex?: number;
    isExit?: boolean;
};

export const getUpdateActionType = (
    activeForm: Form,
    check: boolean = true,
    recordEditState: RecordEditState,
    checkParams: CheckFieldParams = {}
) => {
    if (activeForm) {
        const {activeField, currentRow, searchModeLevel, form} = activeForm;

        const saveHtml = form?.Ishtml && checkParams?.isExit;

        if (saveHtml) return UpdateActions.saveHtml;

        const {newRowIndex, isExit} = checkParams;
        const {currentValue, origValue, columnId} = activeField() || {};
        const fieldReadonly = activeForm.getColumnByID(columnId)?.isReadonly;

        const shouldUpdate =
            !fieldReadonly &&
            (recordEditState === RecordEditState.error ||
                recordEditState === RecordEditState.shouldUpdate);

        if (searchModeLevel || !check) return UpdateActions.omit;

        if (
            shouldUpdate ||
            (currentValue !== null && currentValue !== origValue && !fieldReadonly)
        ) {
            /* if we get an error after some field update/save request, we should cal again the `update` request
               with a fixed value even if this value === default value. In another case, the user won't be able to
               work with form */
            if (newRowIndex !== currentRow || isExit) return UpdateActions.update_save;
            return UpdateActions.update;
        }

        /* In case, we got error on read-only field.
            Or entered edit mode by opening select dropdown (whithout further changes)
        */
        if (
            (recordEditState === RecordEditState.update ||
                recordEditState === RecordEditState.shouldUpdate ||
                recordEditState === RecordEditState.edit) &&
            newRowIndex !== currentRow
        ) {
            return UpdateActions.save;
        }
    }

    return UpdateActions.omit;
};

export const createEmptyRow = (columns: Array<Column>): priority.netitems.form.IRecord => {
    const Fields = columns.map(
        c =>
            new priority.netitems.form.Field({
                ColumnID: c.ID,
                Value: null
            })
    );
    return {Fields};
};

export function findFormByLevel(form: Form, level: number) {
    if (form) {
        if (form.level === level) {
            return form;
        }
        if (form.activeSubForm) {
            return findFormByLevel(form.activeSubForm, level);
        }
    }
    return null;
}

export const MAX_FIELDS_PER_COLUMN = 10;

export type TabSectionControl = {
    Column: Column;
    ControlRowIndex: number;
    SpaceGap: boolean;
};

export type TabSection = {
    TabColumnIndex: number;
    Controls: Array<TabSectionControl>;
};

export type ColumnsLayoutTab = {
    TabId: number;
    TabTitle: string;
    TabSection: Array<TabSection>;
};

export const createTemporarilyRecord = (currentForm: Form) => {
    const {currentRow, records} = currentForm;
    const currentRecord = records[currentRow];
    const isNotNew = !!currentRecord?.keys;

    if (isNotNew) {
        const nextRowIndex = currentRow + 1;
        const nextRowIsEmpty = records[nextRowIndex]?.isEmpty();

        if (!nextRowIsEmpty) {
            currentForm.insertEmptyRecord(currentRow, true);
        }

        runInAction(() => {
            currentForm.setCurrentRow(currentRow + 1);
            currentForm.setCurrentColumnIdByIndex(0);
            currentForm?.activeSubForm?.resetRecords();
        });
    }
};

export const findDefaultTab = (
    data: IFormViewTab[],
    activeColumnId: number,
    skipFirstTab: boolean = true
) => {
    return (
        data.find((tab, i) => {
            if (skipFirstTab && i === 0) return false; // skip the first tab cause it's always visible

            return tab.Sections.some(section => {
                return section.Rows.some(row => {
                    return row.some(control => {
                        return control.Column.ID === activeColumnId;
                    });
                });
            });
        }) || data[1]
    );
};
